import { FC, ReactNode, useState } from 'react';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import ListTable from '../../../components/ListTable';
import ListPagination from '../../../components/ListPagination';
import { colors, fontWeights } from '../../../theme/tokens';
import { VerificationEntry, useVerificationHistory } from './useVerificationHistory';

// The public verifications of this document (L2-VQR-05), under its record,
// for administrators: when its code was consulted, as what, how (scanned or
// typed on the verification page) and from which device.

const COLUMNS = ['Date', 'Code consulté', 'Canal', 'Appareil'] as const;

const CHANNEL_LABELS: Record<VerificationEntry['channel'], string> = {
  scan: 'Scan du QR',
  page: 'Page de vérification',
};

const formatDate = (value: string): string => {
  const date = dayjs(value);
  return date.isValid() ? date.format('DD/MM/YYYY - HH[h]mm:ss') : '—';
};

const Code: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
    {children}
  </Box>
);

const VerificationHistory: FC<{ documentId: string }> = ({ documentId }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { data, isLoading, isError, refetch } = useVerificationHistory({
    documentId,
    page,
    pageSize,
  });

  const entries = data?.entries ?? [];
  const total = data?.totalItems ?? 0;

  return (
    <Box component="section" aria-labelledby="verification-history-title">
      <Typography
        id="verification-history-title"
        component="h2"
        sx={{ fontSize: '1.125rem', fontWeight: fontWeights.semiBold, mb: 1.5 }}
      >
        Vérifications publiques{data ? ` (${total})` : ''}
      </Typography>

      <ListTable
        ariaLabel="Vérifications publiques du document"
        columns={COLUMNS}
        actionsWidth={0}
        minWidth={480}
        isLoading={isLoading}
        isError={isError}
        isEmpty={entries.length === 0}
        errorText="Impossible de charger les vérifications de ce document."
        emptyText="Ce document n'a pas encore été vérifié."
        onRetry={() => refetch()}
      >
        {entries.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.at)}</TableCell>
            <TableCell>
              <Code>{entry.code}</Code>
            </TableCell>
            <TableCell>{CHANNEL_LABELS[entry.channel]}</TableCell>
            <TableCell sx={{ color: entry.device ? undefined : colors.muted }}>
              {entry.device ?? 'Inconnu'}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </ListTable>

      {total > 0 && (
        <ListPagination
          count={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}
    </Box>
  );
};

export default VerificationHistory;
